import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Alert } from 'rsuite';

import { AlertWithTimestamp } from '../../../../../../components/AlertWithTimestamp';
import { getPriceArchitectureConfig, savePriceArchitectureConfig } from '../service';
import {
  DEFAULT_PRICE_ARCHITECTURE_VALUES,
  PRICE_ARCHITECTURE_QUERY_KEY,
  PriceArchitectureConfigResponse,
  PriceArchitectureFormValues,
  SavePriceArchitectureConfigPayload,
  UsePriceArchitectureConfigReturn,
} from '../types';

const toFormValues = ({ enabled, applyPsychologicalPricing }: PriceArchitectureConfigResponse): PriceArchitectureFormValues => ({
  enabled,
  applyPsychologicalPricing,
});

export const usePriceArchitectureConfig = (): UsePriceArchitectureConfigReturn => {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<PriceArchitectureFormValues>(DEFAULT_PRICE_ARCHITECTURE_VALUES);
  const [lastSavedValues, setLastSavedValues] = useState<PriceArchitectureFormValues>(DEFAULT_PRICE_ARCHITECTURE_VALUES);

  const query = useQuery({
    queryKey: PRICE_ARCHITECTURE_QUERY_KEY,
    queryFn: getPriceArchitectureConfig,
    retry: false,
    onError: () => {
      AlertWithTimestamp({ message: 'Erro ao carregar as configurações' });
    },
  });

  useEffect(() => {
    if (!query.data) return;

    const nextValues = toFormValues(query.data);
    setValues(nextValues);
    setLastSavedValues(nextValues);
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: savePriceArchitectureConfig,
    onSuccess: (response, payload) => {
      const savedValues = response ? toFormValues(response) : payload;
      const savedResponse = response ?? payload;

      setValues(savedValues);
      setLastSavedValues(savedValues);
      queryClient.setQueryData(PRICE_ARCHITECTURE_QUERY_KEY, savedResponse);
      Alert.success('Configurações salvas com sucesso');
    },
    onError: () => {
      AlertWithTimestamp({ message: 'Erro ao salvar as configurações' });
    },
  });

  const save = async () => {
    const payload: SavePriceArchitectureConfigPayload = {
      ...values,
    };

    await mutation.mutateAsync(payload);
  };

  return {
    values,
    isLoading: query.isLoading,
    isSaving: mutation.isLoading,
    error: query.error ?? mutation.error,
    setEnabled: enabled => setValues(current => ({ ...current, enabled })),
    setApplyPsychologicalPricing: applyPsychologicalPricing => setValues(current => ({ ...current, applyPsychologicalPricing })),
    reset: () => setValues({ ...lastSavedValues }),
    save,
  };
};
